/**
 * WHAT THE MATCH SAYS OUT LOUD, as a pure state machine.
 *
 * §6 asks for live regions on "state changes that matter (mana full, tower
 * destroyed, match result)". The match is a CANVAS: there is no DOM node whose
 * text a screen reader can watch, so those three have to be detected and
 * spoken deliberately. This file is the detection half — no DOM, no i18n, no
 * Pixi, no imports at all.
 *
 * ── IT IS A DIFF, NOT A HOOK ────────────────────────────────────────────────
 * The renderer already computes everything needed as `ViewState` fields that it
 * refreshes every tick. Rather than adding event callbacks to the sim (HARD LAW
 * 3 — no gameplay logic outside `@rga/sim`, and an announcement is not
 * gameplay), the announcer is handed a SNAPSHOT each frame and emits the
 * transitions it has not emitted yet. That makes it trivially testable: feed it
 * two snapshots, assert the sentence.
 *
 * ── WHY EACH RULE IS THE RULE ───────────────────────────────────────────────
 *
 * MANA FULL is announced on the RISING EDGE ONLY, and then not again until the
 * bar has actually dropped. A player at full mana who spends nothing sits at
 * the cap for the rest of the match; announcing per frame, or even per tick,
 * would make the region a metronome that drowns out the two messages that
 * matter. It is also POLITE: mana full is an opportunity, not an emergency, and
 * interrupting the reader mid-sentence to say so is worse than saying it a beat
 * late.
 *
 * A TOWER FALLING is ASSERTIVE and is announced per tower, with WHOSE it was
 * and WHAT IS LEFT. "Your bastion fell — one tower left" is actionable;
 * "tower destroyed" is not, because the two sides' towers fall for opposite
 * reasons. The keep is named separately from a bastion because losing the keep
 * ends the match and losing a bastion does not.
 *
 * THE RESULT is ASSERTIVE and is announced EXACTLY ONCE, latched. A match can
 * report `finished` for many frames while the end cinematic plays, and online
 * the server's `match_end` and the local prediction both arrive; the latch is
 * what makes those one announcement instead of two hundred.
 *
 * THE LAST TEN SECONDS get one polite announcement, on the edge. §6 already
 * gives that window a visual and audio treatment (the timer turns urgent, the
 * heartbeat starts); a player who can use neither gets the same warning here.
 *
 * ── WHAT IS DELIBERATELY NOT ANNOUNCED ──────────────────────────────────────
 * Unit deployments, damage, and the position of anything. A live region is a
 * one-line channel with no interruption model: filling it with the board state
 * makes the three messages that matter unhearable, and a real answer for
 * "where is everything" is a navigable representation of the board, which is a
 * much larger piece of work than this phase's structure pass.
 */

/* ═══════════════════════════════════════════════════════════ the output ══ */

/** i18n keys, so the pure layer never holds a translated string. */
export const MATCH_A11Y_KEYS = Object.freeze({
  manaFull: 'a11y.match.mana_full',
  keepLostFriendly: 'a11y.match.keep_lost_friendly',
  keepLostHostile: 'a11y.match.keep_lost_hostile',
  towerLostFriendly: 'a11y.match.tower_lost_friendly',
  towerLostHostile: 'a11y.match.tower_lost_hostile',
  lastTenSeconds: 'a11y.match.last_ten_seconds',
  resultVictory: 'a11y.match.result_victory',
  resultDefeat: 'a11y.match.result_defeat',
  resultDraw: 'a11y.match.result_draw',
});

export interface A11yAnnouncement {
  /** A key in `MATCH_A11Y_KEYS`. Translated by the caller through `t()`. */
  readonly key: string;
  /** `t()` placeholders. Counts only — never a player-supplied string. */
  readonly params: Readonly<Record<string, number>>;
  /** `true` interrupts the reader. Towers and the result, and nothing else. */
  readonly assertive: boolean;
}

/* ═════════════════════════════════════════════════════════ the input ══ */

/**
 * Everything the announcer reads, and nothing else.
 *
 * Every field is FROM THE VIEWER'S SIDE — "friendly" is the player looking at
 * the screen. The adapter that builds this from a `ViewState` is the only code
 * that has to know which `Side` that is, which keeps the sides out of this file
 * entirely and makes a mirrored/spectated view a property of the adapter.
 *
 * `towersFriendly` / `towersHostile` are alive-flags indexed by tower slot,
 * with SLOT 0 THE KEEP (the sim's own ordering — `board.ts` places
 * `TOWER_KEEP` at slot 0 and the two bastions at 1 and 2, and
 * `MatchState.bastionsAlive` counts from index 1 for the same reason).
 */
export interface MatchA11ySnapshot {
  /** The viewer's mana bar is at maximum right now. */
  readonly manaFull: boolean;
  readonly towersFriendly: readonly boolean[];
  readonly towersHostile: readonly boolean[];
  readonly lastTenSeconds: boolean;
  /** The match is over. */
  readonly finished: boolean;
  readonly playerWon: boolean;
  readonly draw: boolean;
}

export const EMPTY_ANNOUNCEMENTS: readonly A11yAnnouncement[] = Object.freeze([]);

/** Shared, frozen, for the announcements that interpolate nothing. */
const NO_PARAMS: Readonly<Record<string, number>> = Object.freeze({});

/**
 * Copy `src` into `dst` in place.
 *
 * `dst.length = ...` plus an index loop, rather than `[...src]`, because
 * `update()` runs once per RENDERED FRAME. A spread here would allocate two
 * arrays sixty times a second for the entire length of a match, on the hot path
 * `engine/match/perf.ts` measures and `ViewState.rebuild` is written to keep
 * allocation-free. The tower count never changes mid-match, so after the first
 * frame this loop writes three booleans and allocates nothing at all.
 */
function copyInto(dst: boolean[], src: readonly boolean[]): void {
  if (dst.length !== src.length) dst.length = src.length;
  for (let i = 0; i < src.length; i++) dst[i] = src[i] === true;
}

function aliveCount(towers: readonly boolean[]): number {
  let n = 0;
  for (const alive of towers) if (alive) n++;
  return n;
}

/* ══════════════════════════════════════════════════════════ the machine ══ */

/**
 * Diffs successive snapshots into announcements.
 *
 * One instance per match. `update()` is called from the render loop, so it
 * allocates NOTHING on the overwhelmingly common path where nothing changed —
 * it returns the shared frozen empty array.
 */
export class MatchAnnouncer {
  private manaWasFull = false;
  private lastTenAnnounced = false;
  private resultAnnounced = false;
  /** Alive-flags as last seen, so a fall is an edge and not a level. */
  private readonly prevFriendly: boolean[] = [];
  private readonly prevHostile: boolean[] = [];
  private started = false;
  /**
   * The announcements for the call in progress, allocated on the FIRST push.
   *
   * `update()` runs every rendered frame and emits on almost none of them, so
   * even an empty `[]` per frame is an allocation the render loop does not have
   * to make. `null` here and `EMPTY_ANNOUNCEMENTS` on the way out means a quiet
   * frame allocates nothing at all; the array handed back on a loud one is
   * fresh, so a caller may keep it.
   */
  private pending: A11yAnnouncement[] | null = null;

  /** Forget everything. A rematch in the same screen reuses the instance. */
  reset(): void {
    this.manaWasFull = false;
    this.lastTenAnnounced = false;
    this.resultAnnounced = false;
    this.prevFriendly.length = 0;
    this.prevHostile.length = 0;
    this.started = false;
    this.pending = null;
  }

  private emit(key: string, assertive: boolean, params: Readonly<Record<string, number>>): void {
    (this.pending ??= []).push({ key, params, assertive });
  }

  /**
   * The transitions since the previous call.
   *
   * THE FIRST CALL EMITS NOTHING but the initial state's latches. A match that
   * is reconnected into with a tower already down must not announce it falling
   * — the player was not there, and "your bastion fell" about something that
   * happened two minutes ago is worse than silence.
   */
  update(snap: MatchA11ySnapshot): readonly A11yAnnouncement[] {
    if (!this.started) {
      this.started = true;
      copyInto(this.prevFriendly, snap.towersFriendly);
      copyInto(this.prevHostile, snap.towersHostile);
      this.manaWasFull = snap.manaFull;
      this.lastTenAnnounced = snap.lastTenSeconds;
      // A snapshot that is ALREADY finished on the first frame is a replay
      // opened at the end. Latch it silently for the same reason.
      this.resultAnnounced = snap.finished;
      return EMPTY_ANNOUNCEMENTS;
    }

    /* --- towers, assertive, one per tower ------------------------------- */
    this.diffTowers(snap.towersFriendly, this.prevFriendly, true);
    this.diffTowers(snap.towersHostile, this.prevHostile, false);
    copyInto(this.prevFriendly, snap.towersFriendly);
    copyInto(this.prevHostile, snap.towersHostile);

    /* --- mana, polite, rising edge only --------------------------------- */
    if (snap.manaFull && !this.manaWasFull && !snap.finished) {
      this.emit(MATCH_A11Y_KEYS.manaFull, false, NO_PARAMS);
    }
    this.manaWasFull = snap.manaFull;

    /* --- the clock, polite, once ---------------------------------------- */
    if (snap.lastTenSeconds && !this.lastTenAnnounced && !snap.finished) {
      this.emit(MATCH_A11Y_KEYS.lastTenSeconds, false, NO_PARAMS);
    }
    this.lastTenAnnounced = snap.lastTenSeconds;

    /* --- the result, assertive, latched --------------------------------- */
    if (snap.finished && !this.resultAnnounced) {
      this.resultAnnounced = true;
      const key = snap.draw
        ? MATCH_A11Y_KEYS.resultDraw
        : snap.playerWon
          ? MATCH_A11Y_KEYS.resultVictory
          : MATCH_A11Y_KEYS.resultDefeat;
      this.emit(key, true, NO_PARAMS);
    }

    const out = this.pending;
    this.pending = null;
    return out ?? EMPTY_ANNOUNCEMENTS;
  }

  private diffTowers(
    now: readonly boolean[],
    before: readonly boolean[],
    friendly: boolean,
  ): void {
    for (let slot = 0; slot < now.length; slot++) {
      const was = before[slot] ?? true;
      if (was !== true || now[slot] !== false) continue;
      const keep = slot === 0;
      this.emit(
        keep
          ? friendly
            ? MATCH_A11Y_KEYS.keepLostFriendly
            : MATCH_A11Y_KEYS.keepLostHostile
          : friendly
            ? MATCH_A11Y_KEYS.towerLostFriendly
            : MATCH_A11Y_KEYS.towerLostHostile,
        true,
        // The count AFTER the fall — what the player has left, not what they
        // had. Allocated only on a tower falling, which is at most six times
        // in a whole match.
        { remaining: aliveCount(now) },
      );
    }
  }
}
